import { useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const REASONS = [
  { value: "prohibited", label: "Prohibited or unsafe product" },
  { value: "biosecurity", label: "Biosecurity concern" },
  { value: "misleading", label: "Misleading listing" },
  { value: "scam", label: "Suspected scam" },
  { value: "ip", label: "Intellectual property concern" },
  { value: "other", label: "Other" },
];

export function ReportListingLink({ productId, productName, storeSlug, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={`report-listing-open-${productId || storeSlug || "gate"}`}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-mono-label text-[#6B6E67] hover:text-[#20251F] transition-colors ${className}`}
      >
        <Flag className="w-3 h-3" /> Something wrong with this Gate?
      </button>
      <ReportListingDialog
        open={open}
        onOpenChange={setOpen}
        productId={productId}
        productName={productName}
        storeSlug={storeSlug}
      />
    </>
  );
}

export default function ReportListingDialog({ open, onOpenChange, productId, productName, storeSlug }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) return;
    setBusy(true);
    try {
      await api.post("/report-listing", {
        product_id: productId || null,
        store_slug: storeSlug || null,
        reason,
        details: details.trim() || null,
        reporter_email: email.trim() || null,
      });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    // reset after close animation
    setTimeout(() => {
      setSent(false); setReason(""); setDetails(""); setEmail("");
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="sm:max-w-md" data-testid="report-listing-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-heading text-2xl">
            {sent ? "Thanks — we'll take a look." : "Something wrong with this Gate?"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {sent ? "Your report has been received." : "Flag this listing to the Gate + Gather team. The seller isn't notified."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="pt-2" data-testid="report-listing-success">
            <p className="text-sm text-stone-700 leading-relaxed">
              A quiet note has landed with the Gate + Gather team. Someone real will look at it. Thanks for helping keep the platform trustworthy.
            </p>
            <Button onClick={close} data-testid="report-listing-close" className="mt-6 w-full rounded-full bg-[#20251F] hover:bg-black text-white h-11">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2" data-testid="report-listing-form">
            {productName && (
              <p className="text-sm text-stone-600">
                About <span className="font-editorial text-[#20251F]">{productName}</span>
              </p>
            )}
            <div>
              <Label className="mb-2 block">What's the concern?</Label>
              <div className="space-y-1.5">
                {REASONS.map((r) => (
                  <label
                    key={r.value}
                    data-testid={`report-reason-${r.value}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 cursor-pointer transition-colors ${
                      reason === r.value ? "border-[#20251F] bg-[#FFFCF6]" : "border-stone-200 hover:border-stone-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-[#20251F]"
                    />
                    <span className="text-sm text-[#20251F]">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="report-details">Anything you want to add? (optional)</Label>
              <Textarea
                id="report-details"
                data-testid="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                placeholder="A short note helps us take a proper look."
                className="mt-1 resize-none"
                maxLength={2000}
              />
            </div>
            <div>
              <Label htmlFor="report-email">Your email (optional)</Label>
              <Input
                id="report-email"
                data-testid="report-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="So we can come back to you if we need more info"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !reason}
              data-testid="report-listing-submit"
              className="w-full h-11 rounded-full bg-[#20251F] hover:bg-black text-white"
            >
              {busy ? "Sending…" : "Send report"}
            </Button>
            <p className="text-[11px] text-stone-500 leading-relaxed text-center">
              No public drama. The seller isn't notified you reported them.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
